import useSWR from "swr";
import type { components } from "./zz_schema";

import { useAuth } from "@/auth/auth";

export type XcodeSnapshot = components["schemas"]["XcodeSnapshot"];

export function useOrganizationXcodeSnapshots(organizationId?: string) {
	const auth = useAuth();

	return useSWR(organizationId ? `/v1/organizations/${organizationId}/xcode_snapshots` : null, async () => {
		if (!organizationId) return undefined;
		const snapshots: XcodeSnapshot[] = [];
		let offset = 0;
		do {
			const result = await auth.client.GET("/v1/organizations/{organizationId}/xcode_snapshots", {
				params: {
					path: { organizationId },
					query: { limit: 500, offset },
				},
			});
			if (result.error) throw new Error(result.error.message);
			snapshots.push(...result.data.snapshots);
			if (result.data.nextOffset === undefined) break;
			if (result.data.nextOffset <= offset) throw new Error("Invalid snapshot pagination response");
			offset = result.data.nextOffset;
		} while (true);
		return { data: { snapshots }, error: undefined };
	});
}

export function useOrganizationXcodeSnapshot(organizationId: string | undefined, key: string | undefined) {
	const auth = useAuth();
	return useSWR(organizationId && key ? ["xcode-snapshot", organizationId, key] : null, async () => {
		if (!organizationId || !key) return undefined;
		const { data, error, response } = await auth.client.GET("/v1/organizations/{organizationId}/xcode_snapshots/{key}", {
			params: { path: { organizationId, key } },
		});
		if (response.status === 404) return null;
		if (error) throw new Error(error.message);
		return data;
	});
}

export function formatArchiveSize(bytes: number): string {
	if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GiB`;
	if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MiB`;
	if (bytes >= 1024) return `${Math.round(bytes / 1024)} KiB`;
	return `${bytes} B`;
}

// splitXcodeVersion turns "26.4.0.17E192" into its marketing version and build number.
export function splitXcodeVersion(key: string): { version: string; build?: string } {
	const parts = key.split(".");
	if (parts.length < 2) return { version: key };
	const build = parts[parts.length - 1];
	if (!/^[0-9]+[A-Z]/.test(build)) return { version: key };
	const numeric = parts.slice(0, -1);
	// Drop a trailing ".0" patch so 26.4.0 reads as 26.4, as Xcode's About box does.
	if (numeric.length === 3 && numeric[2] === "0") numeric.pop();
	return { version: numeric.join("."), build };
}

export function shortInstanceId(id: string): string {
	return id.length > 12 ? `${id.slice(0, id.indexOf("_", id.indexOf("_") + 1) + 1)}…${id.slice(-5)}` : id;
}
